import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/client';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { email: string; groups: string[] } | null;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  getToken: () => string | null;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const POOL_ID = import.meta.env.VITE_COGNITO_POOL_ID || '';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';

// Extract region from pool ID (e.g., "ap-southeast-1_So4AvRN4m" -> "ap-southeast-1")
const REGION = POOL_ID.split('_')[0] || 'ap-southeast-1';
const COGNITO_URL = `https://cognito-idp.${REGION}.amazonaws.com/`;

const TOKEN_KEY = 'villa_admin_tokens';

interface StoredTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
}

function storeTokens(tokens: StoredTokens) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

function loadTokens(): StoredTokens | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const payload = token.split('.')[1];
  return JSON.parse(atob(payload));
}

async function cognitoAuth(email: string, password: string): Promise<{ IdToken: string; AccessToken: string; RefreshToken: string; ExpiresIn: number }> {
  const resp = await fetch(COGNITO_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: CLIENT_ID,
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password,
      },
    }),
  });

  const data = await resp.json();
  if (data.__type || !data.AuthenticationResult) {
    throw new Error(data.message || 'Authentication failed');
  }
  return data.AuthenticationResult;
}

async function cognitoRefresh(refreshToken: string): Promise<{ IdToken: string; AccessToken: string; ExpiresIn: number }> {
  const resp = await fetch(COGNITO_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: CLIENT_ID,
      AuthParameters: {
        REFRESH_TOKEN: refreshToken,
      },
    }),
  });

  const data = await resp.json();
  if (data.__type || !data.AuthenticationResult) {
    throw new Error(data.message || 'Token refresh failed');
  }
  return data.AuthenticationResult;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });
  const [tokens, setTokens] = useState<StoredTokens | null>(null);

  const getToken = useCallback(() => {
    return tokens?.idToken ?? null;
  }, [tokens]);

  useEffect(() => {
    api.setTokenGetter(getToken);
  }, [getToken]);

  const refreshAndGetToken = useCallback(async (): Promise<string | null> => {
    const stored = loadTokens();
    if (!stored?.refreshToken) return null;
    try {
      const result = await cognitoRefresh(stored.refreshToken);
      const newTokens: StoredTokens = {
        idToken: result.IdToken,
        accessToken: result.AccessToken,
        refreshToken: stored.refreshToken,
        expiresAt: Date.now() + result.ExpiresIn * 1000,
      };
      storeTokens(newTokens);
      setTokens(newTokens);
      const payload = decodeJwtPayload(result.IdToken);
      setState({
        isAuthenticated: true,
        isLoading: false,
        user: {
          email: (payload.email as string) || (payload.sub as string),
          groups: (payload['cognito:groups'] as string[]) || [],
        },
        error: null,
      });
      return result.IdToken;
    } catch {
      clearTokens();
      setTokens(null);
      setState({ isAuthenticated: false, isLoading: false, user: null, error: 'Session expired' });
      return null;
    }
  }, []);

  useEffect(() => {
    api.setTokenRefresher(refreshAndGetToken);
  }, [refreshAndGetToken]);

  // Restore session from localStorage on mount
  useEffect(() => {
    if (!POOL_ID || !CLIENT_ID) {
      setState({ isAuthenticated: true, isLoading: false, user: { email: 'dev@local', groups: ['admin'] }, error: null });
      return;
    }

    const stored = loadTokens();
    if (!stored) {
      setState((s) => ({ ...s, isLoading: false }));
      return;
    }

    // If expired, try refresh
    if (Date.now() > stored.expiresAt) {
      cognitoRefresh(stored.refreshToken)
        .then((result) => {
          const newTokens: StoredTokens = {
            idToken: result.IdToken,
            accessToken: result.AccessToken,
            refreshToken: stored.refreshToken,
            expiresAt: Date.now() + result.ExpiresIn * 1000,
          };
          storeTokens(newTokens);
          setTokens(newTokens);
          const payload = decodeJwtPayload(result.IdToken);
          setState({
            isAuthenticated: true,
            isLoading: false,
            user: {
              email: (payload.email as string) || (payload.sub as string),
              groups: (payload['cognito:groups'] as string[]) || [],
            },
            error: null,
          });
        })
        .catch(() => {
          clearTokens();
          setState((s) => ({ ...s, isLoading: false }));
        });
      return;
    }

    // Valid stored tokens
    setTokens(stored);
    const payload = decodeJwtPayload(stored.idToken);
    setState({
      isAuthenticated: true,
      isLoading: false,
      user: {
        email: (payload.email as string) || (payload.sub as string),
        groups: (payload['cognito:groups'] as string[]) || [],
      },
      error: null,
    });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    if (!POOL_ID || !CLIENT_ID) {
      setState({ isAuthenticated: true, isLoading: false, user: { email, groups: ['admin'] }, error: null });
      return;
    }

    let result;
    try {
      result = await cognitoAuth(email, password);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Authentication failed';
      setState((s) => ({ ...s, error: msg }));
      return;
    }
    const newTokens: StoredTokens = {
      idToken: result.IdToken,
      accessToken: result.AccessToken,
      refreshToken: result.RefreshToken,
      expiresAt: Date.now() + result.ExpiresIn * 1000,
    };
    storeTokens(newTokens);
    setTokens(newTokens);
    const payload = decodeJwtPayload(result.IdToken);
    setState({
      isAuthenticated: true,
      isLoading: false,
      user: {
        email: (payload.email as string) || (payload.sub as string),
        groups: (payload['cognito:groups'] as string[]) || [],
      },
      error: null,
    });
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setTokens(null);
    setState({ isAuthenticated: false, isLoading: false, user: null, error: null });
  }, []);

  const changePassword = useCallback(async (_oldPassword: string, _newPassword: string) => {
    // Requires access token — would need GlobalSignOut + re-auth pattern
    throw new Error('Change password not implemented yet');
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, getToken, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
